import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, StatusBadge } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnerPlans, setPartnerPlans] = useState([]);
  const [institutes, setInstitutes] = useState([]);

  const load = () => {
    api.get("/admin/users").then(({ data }) => setUsers(data.users)).catch(() => {});
    api.get("/admin/employers").then(({ data }) => setEmployers(data.employers)).catch(() => {});
    api.get("/admin/partners").then(({ data }) => {
      setPartners(data.partners);
      setPartnerPlans(data.partner_plans || []);
    }).catch(() => {});
    api.get("/admin/institutes").then(({ data }) => setInstitutes(data.institutes)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const assignPlan = async (id, plan_id) => {
    try {
      await api.post(`/admin/partners/${id}/plan`, { plan_id });
      toast.success("Partner plan activated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/users/${id}/status`, { status });
      toast.success(`User ${status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const review = async (kind, id, action) => {
    try {
      await api.post(`/admin/${kind}/${id}/review`, { action });
      toast.success(`${kind.slice(0, -1)} ${action}d`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-users-page">
      <Tabs defaultValue="users">
        <TabsList className="mb-6">
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="employers" data-testid="tab-employers">Employers</TabsTrigger>
          <TabsTrigger value="partners" data-testid="tab-partners">Partners</TabsTrigger>
          <TabsTrigger value="institutes" data-testid="tab-institutes">Institutes</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-[#F8FAFC] border-b border-border/60 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-5 py-3.5 font-semibold">User</th>
                  <th className="px-5 py-3.5 font-semibold">Role</th>
                  <th className="px-5 py-3.5 font-semibold">Status</th>
                  <th className="px-5 py-3.5 font-semibold">Joined</th>
                  <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border/40" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-[#0B1B3D]">{u.name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </td>
                    <td className="px-5 py-4 text-xs capitalize">{(u.role || "").replace(/_/g, " ")}</td>
                    <td className="px-5 py-4"><StatusBadge status={u.status} /></td>
                    <td className="px-5 py-4 text-xs text-muted-foreground">{fmtDate(u.created_at)}</td>
                    <td className="px-5 py-4 text-right">
                      {u.role !== "super_admin" && (
                        u.status === "active" ? (
                          <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => setStatus(u.id, "suspended")} data-testid={`suspend-${u.id}`}>Suspend</Button>
                        ) : (
                          <Button size="sm" variant="outline" className="rounded-full text-emerald-700 border-emerald-200" onClick={() => setStatus(u.id, "active")} data-testid={`activate-${u.id}`}>Reactivate</Button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="employers">
          {employers.length === 0 ? <EmptyState title="No employers yet" testId="employers-empty" /> : (
            <div className="space-y-3">
              {employers.map((e) => (
                <div key={e.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`employer-${e.id}`}>
                  <div>
                    <p className="font-semibold text-[#0B1B3D]">{e.company_name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{e.user?.email} · KYC: {e.kyc_status}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {e.subscription ? (
                      <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1 font-semibold">
                        {e.subscription.plan_name} · active
                      </span>
                    ) : (
                      <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1">No subscription</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="partners">
          {partners.length === 0 ? <EmptyState title="No partners yet" testId="partners-empty" /> : (
            <div className="space-y-3">
              {partners.map((p) => (
                <div key={p.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`partner-${p.id}`}>
                  <div>
                    <p className="font-semibold text-[#0B1B3D]">{p.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{p.submissions} submissions · {p.placements} placements</p>
                    <p className="text-xs mt-1">
                      {p.subscription?.status === "active" ? (
                        <span className="text-emerald-700 font-semibold" data-testid={`partner-plan-${p.id}`}>
                          {p.subscription.plan_name} · expires {p.subscription.expires_at?.slice(0, 10)}
                        </span>
                      ) : (
                        <span className="text-slate-500" data-testid={`partner-plan-${p.id}`}>No active plan</span>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={p.status} />
                    <Select onValueChange={(v) => assignPlan(p.id, v)}>
                      <SelectTrigger className="w-56" data-testid={`plan-select-${p.id}`}><SelectValue placeholder="Assign plan (1 yr)" /></SelectTrigger>
                      <SelectContent>
                        {partnerPlans.map((pl) => (
                          <SelectItem key={pl.id} value={pl.id}>{pl.name} — ₹{pl.price_yearly.toLocaleString("en-IN")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {p.status === "pending" && (
                      <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => review("partners", p.id, "reject")} data-testid={`reject-partner-${p.id}`}>Reject</Button>
                    )}
                    {p.status === "active" ? (
                      <Button size="sm" variant="outline" className="rounded-full text-amber-700 border-amber-300" onClick={() => review("partners", p.id, "deactivate")} data-testid={`deactivate-partner-${p.id}`}>Deactivate</Button>
                    ) : (
                      <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => review("partners", p.id, "approve")} data-testid={`approve-partner-${p.id}`}>Activate</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="institutes">
          {institutes.length === 0 ? <EmptyState title="No institutes yet" testId="institutes-empty" /> : (
            <div className="space-y-3">
              {institutes.map((i) => (
                <div key={i.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`institute-${i.id}`}>
                  <div>
                    <p className="font-semibold text-[#0B1B3D]">{i.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{i.courses} courses</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={i.status} />
                    {i.status === "pending" && (
                      <>
                        <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => review("institutes", i.id, "approve")} data-testid={`approve-institute-${i.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => review("institutes", i.id, "reject")} data-testid={`reject-institute-${i.id}`}>Reject</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
